import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { SOURCE_RCS } from '@rsos/constants/locationSources';
import { TEXT } from '@rsos/constants/locationTypes';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_ACCEPT_STATUS_NONE,
  RCS_ACCEPT_STATUS_TIMED_OUT,
  RCS_ACCEPT_STATUS_DECLINED,
} from '@rsos/constants/rcsStatuses';
import { useFlag } from '@rsos/flags';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasAccessToGoogleRCS from '@rsos/hooks/useHasAccessToGoogleRCS';
import { useGetConversationMetaData } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { setCallExpired } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  useGetRcsConversationMetadataQuery,
  useLazyTimeoutRcsConversationQuery,
} from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import {
  setRcsAcceptStatus,
  dismissRcsToast,
} from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { getTokens } from '@rsos/sinatra';
import { formatPhoneWithDashes } from '@rsos/utils';
import getCallStatusName from '@rsos/utils/getCallStatusName';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { getExpiredCallAge } from '@rsos/utils/metaTags';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import usePortalType from '@rsos/utils/usePortalType';
import { RCS_TIMEOUT } from '../../../constants/rcsStatus';
import isTextBasedSource from '../../../utils/isSourceSMS';
import {
  LiveVideoSession,
  VideoTo911Session,
  MediaAvailable,
  ZeroEyesEvent,
} from '../../AdditionalData/AdrQuery/QueueText';
import OnsitePersonnelNotifiedText from '../OnsitePersonnelNotifiedText';
import {
  Container,
  IconBox,
  CallBox,
  PhoneNumber,
  PhoneDetails,
  CallStatusTag,
  CallStatus,
  AirAmbulanceStatus,
  TextMessage,
  SiriusXMMessage,
  TF911NewMessage,
  RcsNewMessage,
  RegularNewMessage,
  TimeBox,
  Time,
  RemoveCall,
  ConnectedSiteMessage,
  CollaboratorMessage,
} from './EmergencyCall.styles';
import EmergencyCallIcons from './EmergencyCallIcons';
import TextMessageStatus from './TextMessageStatus';
import {
  trackViewRCSToast,
  trackCloseRCSToast,
  trackRcsTimeout,
} from './emergencyCallTrackings';
import { fetchLocationShareLocation } from './fetchLocationSharedLocation';
import { getCollaboratorTypeDisplayName, toastRcsAvailable } from './helpers';
import useFullSelectedNumberData from './hooks/useFullSelectedNumberData';

const expiredCallAge = getExpiredCallAge();

const EmergencyCall = ({
  applicationType = '',
  classOfService = '',
  callerID = '',
  callStatus = '',
  rightClickedCallerID = '',
  hasAgent511Access = false,
  hasGoogleCarCrashData = false,
  hasGoogleCallerInfo = false,
  isApple = false,
  isConnectedCar = false,
  isCallerIn911informBuilding = false,
  isCallerInConnectedSitesBuilding = false,
  isCallReceived = false,
  isCallShared = false,
  isInternational = false,
  isManualSearch = false,
  isMapMarkerHovered = false,
  isRemoveCallVisible = false,
  isSearchResult = false,
  isSearchResultHovered = false,
  isSelected = false,
  isTT911 = false,
  isAdmin = false,
  isUnread = false,
  isUnseen = false,
  onRemoveCall = () => {},
  setCallerID = () => {},
  source = '',
  tt911ADR = null,
  previousCallsCount = 0,
  ui = {},
  onViewRcsEvent = () => {},
}) => {
  const dispatch = useDispatch();
  const eventShareData = useSelector(state => state.eventShare[callerID]) || [];
  const token = getTokens()?.token;

  const { isPremiumUser } = usePortalType();
  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();

  const tt911Message = tt911ADR?.data?.message;
  const [googleIndicator, setGoogleIndicator] = useState(null);
  const [displayRemoveCall, setDisplayRemoveCall] =
    useState(isRemoveCallVisible);
  const airAmbulanceCallsRequests = useSelector(
    state => state.airAmbulance?.requests?.calls || {},
  );
  const callWithAirAmbulanceRequest = airAmbulanceCallsRequests[callerID];

  const isConversationUnread = useSelector(
    state => state.conversations?.[callerID]?.isUnread,
  );

  const collaborator = useSelector(
    state => state.emergencyCalls.callQueue[callerID]?.collaborator,
  );
  const isCollaborator = !!collaborator?.type;
  const isCollaboratorUnread = collaborator?.isUnread;

  const callStatusDisplayName = useMemo(() => {
    if (hasAirAmbulanceAccess && callWithAirAmbulanceRequest) {
      const statuses = callWithAirAmbulanceRequest.body?.status || [];
      const statusName = statuses[statuses.length - 1];
      return statusName?.update || '';
    }
    const callStatusName = getCallStatusName(callStatus);
    return callStatusName;
  }, [callStatus, callWithAirAmbulanceRequest, hasAirAmbulanceAccess]);

  const [rcsSecondsLeft, setRcsSecondsLeft] = useState(RCS_TIMEOUT);
  const [rcsTimerRunning, setRcsTimerRunning] = useState(false);
  const rcsConversation = useSelectedRcsConversation(callerID);

  // get RCS conversation metadata for tracking
  const rcsConversationId = useMemo(
    () => rcsConversation?.data?.conversation_id,
    [rcsConversation],
  );

  const rcsSessionId = useMemo(
    () => rcsConversation?.data?.session_id,
    [rcsConversation],
  );

  const [triggerTimeoutRcsConversation] = useLazyTimeoutRcsConversationQuery();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      )?.name,
  );

  const animateProps = useSpring({
    config: { duration: 200 },
    display: displayRemoveCall ? 'flex' : 'none',
    opacity: displayRemoveCall ? 1 : 0,
  });

  useEffect(() => {
    rightClickedCallerID === callerID
      ? setDisplayRemoveCall(true)
      : setDisplayRemoveCall(false);
  }, [rightClickedCallerID, callerID]);

  useEffect(() => {
    if (hasGoogleCallerInfo && hasGoogleCarCrashData) {
      setGoogleIndicator('Language, Crash Detection Sensor');
    } else if (hasGoogleCallerInfo) {
      setGoogleIndicator('Language');
    } else if (hasGoogleCarCrashData) {
      setGoogleIndicator('Crash Detection Sensor');
    }
  }, [hasGoogleCallerInfo, hasGoogleCarCrashData]);

  const hasGoogleRCSAccess = useHasAccessToGoogleRCS();

  const fullSelectedNumberData = useFullSelectedNumberData(callerID);

  const mostRecentLocation = useMemo(
    () => getMostRecentLoc(fullSelectedNumberData.callData),
    [fullSelectedNumberData],
  );

  const elsSource = useMemo(() => {
    if (!mostRecentLocation) return;
    return mostRecentLocation.source;
  }, [mostRecentLocation]);

  const isSMS = useMemo(
    () =>
      fullSelectedNumberData?.callData?.isTT911 ||
      !!fullSelectedNumberData?.textFrom911Data?.data?.conversation_id ||
      isTextBasedSource(mostRecentLocation?.source) ||
      mostRecentLocation?.class_of_service_category === TEXT,
    [
      fullSelectedNumberData?.callData?.isTT911,
      fullSelectedNumberData?.textFrom911Data?.data?.conversation_id,
      mostRecentLocation?.source,
      mostRecentLocation?.class_of_service_category,
    ],
  );

  const isExpired = useMemo(() => {
    return !!fullSelectedNumberData?.callData?.isCallExpired;
  }, [fullSelectedNumberData?.callData?.isCallExpired]);

  const rcsStatusText = useMemo(() => {
    if (!rcsConversation || !rcsConversation.data) return '';
    const { accept_status } = rcsConversation.data;
    if (accept_status === RCS_ACCEPT_STATUS_NONE) {
      if (rcsSecondsLeft > 0) {
        return `RCS AVAILABLE FOR ${rcsSecondsLeft} SEC`;
      }
      return `RCS TIMED OUT`;
    }
    if (accept_status === RCS_ACCEPT_STATUS_TIMED_OUT) return 'RCS TIMED OUT';
    if (accept_status === RCS_ACCEPT_STATUS_DECLINED) return 'RCS DECLINED';
    if (accept_status === RCS_ACCEPT_STATUS_ACCEPTED) return 'RCS MESSAGE';
    return 'RCS MESSAGE';
  }, [rcsConversation, rcsSecondsLeft]);

  // use separate unread tracking instead of conversation UI state
  const rcsUnreadMessages = useSelector(
    state => state.rcs?.unreadMessages || {},
  );
  const unreadRcsMessage = useMemo(() => {
    if (
      rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_TIMED_OUT ||
      rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_DECLINED
    ) {
      return false; // no indicators for timed out or declined conversations
    }

    const separateTracking = rcsUnreadMessages[callerID];
    if (separateTracking !== undefined) {
      return separateTracking;
    }

    // fallback for edge cases
    if (!rcsConversation || !rcsConversation.ui) return false;
    const { unseenMessage } = rcsConversation.ui;
    return unseenMessage;
  }, [rcsConversation, rcsUnreadMessages, callerID]);

  const unreadTF911Message = useMemo(() => {
    if (
      !fullSelectedNumberData?.textFrom911Data ||
      !fullSelectedNumberData?.textFrom911Data.ui
    )
      return false;
    const { hasUnreadMessages } = fullSelectedNumberData.textFrom911Data.ui;
    return hasUnreadMessages || false;
  }, [fullSelectedNumberData]);

  const callStartTime = useMemo(() => {
    if (mostRecentLocation?.call_start_time) {
      return mostRecentLocation.call_start_time;
    } else if (mostRecentLocation?.conversation_start_time) {
      return mostRecentLocation.conversation_start_time;
    } else if (fullSelectedNumberData?.textFrom911Data?.data?.call_start_time) {
      return fullSelectedNumberData.textFrom911Data.data.call_start_time;
    } else if (fullSelectedNumberData?.textFrom911Data?.data?.createdAt) {
      return fullSelectedNumberData.textFrom911Data.data.createdAt;
    }
  }, [fullSelectedNumberData, mostRecentLocation]);

  const checkCallExpired = useCallback(() => {
    const currentTime = new Date().getTime();
    return (currentTime - callStartTime) / 1000 > expiredCallAge;
  }, [callStartTime]);

  const timeExpired = useCallback(() => {
    const currentTime = new Date().getTime();
    const seconds = (currentTime - callStartTime) / 1000;
    let d = Math.floor(seconds / (3600 * 24));
    let h = Math.floor((seconds % (3600 * 24)) / 3600);

    let dDisplay = d > 0 ? d + (d == 1 ? ' day' : ' days') : '';
    let hDisplay = h > 0 ? h + (h == 1 ? ' hour' : ' hours') : '';
    return dDisplay || hDisplay ? `${dDisplay} ${hDisplay} old` : '';
  }, [callStartTime]);

  const handleRemoveCall = useCallback(
    e => {
      e.stopPropagation();
      onRemoveCall({
        caller_id: rightClickedCallerID,
      });
      setDisplayRemoveCall(false);
    },
    [onRemoveCall, rightClickedCallerID],
  );

  // If call is expired, but call start time changed, set it to not expired
  useEffect(() => {
    if (isExpired && !checkCallExpired()) {
      dispatch(setCallExpired({ callerID, isCallExpired: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, callStartTime]);

  useEffect(() => {
    const callExpiredTimer =
      new Date(callStartTime + expiredCallAge * 1000) - new Date();
    // If on mount, the callExpiredTimer is < 0, the callback will be invoked
    const timer = setTimeout(() => {
      if (!isExpired && checkCallExpired()) {
        dispatch(setCallExpired({ callerID, isCallExpired: true }));
      }
    }, callExpiredTimer);

    return () => clearTimeout(timer);
  }, [dispatch, callerID, callStartTime, checkCallExpired, isExpired]);

  const handleViewRcsEventFromToast = useCallback(
    (...params) => {
      onViewRcsEvent(...params);
      trackViewRCSToast({
        conversationId: rcsConversationId,
        sessionId: rcsSessionId,
      });
    },
    [onViewRcsEvent, rcsConversationId, rcsSessionId],
  );

  const handleCloseRcsToast = useCallback(() => {
    trackCloseRCSToast({
      conversationId: rcsConversationId,
      sessionId: rcsSessionId,
    });
    dispatch(dismissRcsToast({ callerID }));
  }, [dispatch, callerID, rcsConversationId, rcsSessionId]);

  useEffect(() => {
    if (!rcsConversation || !rcsConversation.data) return;
    const { accept_status, serverTimeMillis = new Date().getTime() } =
      rcsConversation.data;

    if (accept_status !== RCS_ACCEPT_STATUS_NONE) {
      return;
    }

    // Only set initial timer state if not already running
    if (!rcsTimerRunning) {
      setRcsTimerRunning(true);
      const initialOffset = serverTimeMillis - new Date().getTime();
      const localTime = new Date().getTime();
      const effectiveTime = new Date(localTime + initialOffset);
      const secondsSinceCallStart = Math.max(
        0,
        Math.floor((effectiveTime - callStartTime) / 1000),
      );
      const initialSecondsLeft = RCS_TIMEOUT - secondsSinceCallStart;
      setRcsSecondsLeft(Math.max(0, initialSecondsLeft));
    }

    const interval = setInterval(() => {
      setRcsSecondsLeft(prevSeconds => {
        const newSeconds = Math.max(0, prevSeconds - 1);

        if (newSeconds >= 0) {
          if (!rcsConversation.ui || !rcsConversation.ui.isRcsToastDismissed) {
            if (isSelected) {
              dispatch(dismissRcsToast({ callerID }));
            } else {
              toastRcsAvailable(
                callerID,
                handleViewRcsEventFromToast,
                handleCloseRcsToast,
              );
            }
          }
        }

        if (newSeconds <= 0) {
          if (
            accept_status === RCS_ACCEPT_STATUS_NONE ||
            accept_status === RCS_ACCEPT_STATUS_TIMED_OUT
          ) {
            dispatch(
              setRcsAcceptStatus({
                callerID,
                accept_status: RCS_ACCEPT_STATUS_TIMED_OUT,
              }),
            );
            triggerTimeoutRcsConversation({
              callerID,
              orgName,
            });
            trackRcsTimeout({
              conversationId: rcsConversationId,
              sessionId: rcsSessionId,
            });
          }
          setRcsTimerRunning(false);
          dispatch(dismissRcsToast({ callerID }));
          clearInterval(interval);
        }

        return newSeconds;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [
    callerID,
    dispatch,
    callStartTime,
    onViewRcsEvent,
    isSelected,
    rcsConversation,
    triggerTimeoutRcsConversation,
    orgName,
    handleCloseRcsToast,
    handleViewRcsEventFromToast,
    rcsConversationId,
    rcsSessionId,
    rcsTimerRunning,
  ]);

  const isRcsAvailable = useMemo(() => {
    return (
      hasGoogleRCSAccess &&
      rcsConversation &&
      rcsConversation.data &&
      rcsConversation.data.accept_status === RCS_ACCEPT_STATUS_NONE
    );
  }, [rcsConversation, hasGoogleRCSAccess]);

  const getUnreadStatus = useMemo(() => {
    return (
      isUnread ||
      unreadRcsMessage ||
      unreadTF911Message ||
      isConversationUnread ||
      isCollaboratorUnread ||
      ui.requestedLocationReceived
    );
  }, [
    isUnread,
    unreadRcsMessage,
    unreadTF911Message,
    isConversationUnread,
    isCollaboratorUnread,
    ui,
  ]);

  const hasRequestLocationAccess = useFlag('rsp_request_location');

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID,
    },
    {
      skip:
        !hasGoogleRCSAccess || elsSource !== SOURCE_RCS || !!rcsConversationId,
    },
  );

  useGetConversationMetaData(callerID, orgName);

  useEffect(() => {
    if (hasRequestLocationAccess && isSMS && !mostRecentLocation) {
      dispatch(
        fetchLocationShareLocation({
          callerID,
          orgName,
          token,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequestLocationAccess]);

  return (
    <Container
      isExpired={isExpired}
      isHovered={isMapMarkerHovered || isSearchResultHovered}
      isSearchResult={isSearchResult}
      isSelected={isSelected}
      isUnread={getUnreadStatus}
      isUnseen={isUnseen}
      isRcsAvailable={isRcsAvailable}
      unreadTF911Message={unreadTF911Message}
      unreadRcsMessage={unreadRcsMessage}
      onClick={() => {
        setCallerID(callerID);
        dispatch(setHoveredID(null));
      }}
      isCollaborator={isCollaborator}
    >
      <IconBox>
        <EmergencyCallIcons
          isManualSearch={isManualSearch}
          isCallReceived={isCallReceived}
          isCallShared={isCallShared}
          isSMS={isSMS}
          isSMSReceived={isSMS && isCallReceived}
          isSMSShared={isSMS && isCallShared}
          isUnseen={isUnseen}
          applicationType={applicationType}
          classOfService={classOfService}
          isPremiumUser={isPremiumUser}
          isAdmin={isAdmin}
          collaboratorType={collaborator?.type}
        />
      </IconBox>
      <CallBox>
        <PhoneNumber
          isUnseen={isUnseen}
          unreadTF911Message={unreadTF911Message}
          unreadRcsMessage={unreadRcsMessage}
          isUnread={getUnreadStatus}
        >
          {isInternational ? callerID : formatPhoneWithDashes(callerID)}
          {previousCallsCount > 0 && (
            <span style={{ marginLeft: 4 }}>({previousCallsCount})</span>
          )}
        </PhoneNumber>
        {eventShareData.length > 0 && (
          <RegularNewMessage>Shared Event</RegularNewMessage>
        )}
        {isCollaborator && collaborator?.type && (
          <CollaboratorMessage>
            {getCollaboratorTypeDisplayName(
              collaborator?.type,
              collaborator?.pairedAccountName,
            )}
          </CollaboratorMessage>
        )}
        {ui.requestedLocationReceived && (
          <TF911NewMessage>Location Received</TF911NewMessage>
        )}
        {unreadTF911Message && <TF911NewMessage>NEW MESSAGE</TF911NewMessage>}
        {unreadRcsMessage &&
          !unreadTF911Message &&
          rcsConversation?.data?.accept_status ===
            RCS_ACCEPT_STATUS_ACCEPTED && (
            <RcsNewMessage>NEW RCS MESSAGE</RcsNewMessage>
          )}
        {(isUnread || isUnseen) && !unreadTF911Message && !unreadRcsMessage && (
          <RegularNewMessage>NEW MESSAGE</RegularNewMessage>
        )}

        <LiveVideoSession callerId={callerID} />
        <VideoTo911Session callerId={callerID} />
        <MediaAvailable callerId={callerID} />
        <ZeroEyesEvent callerId={callerID} />

        {isAdmin && <PhoneDetails>Admin Line Call</PhoneDetails>}

        {callStatusDisplayName && !callWithAirAmbulanceRequest && (
          <CallStatusTag status={callStatus} isCallPopup={false}>
            <CallStatus data-name="call-status" status={callStatus}>
              {callStatusDisplayName}
            </CallStatus>
          </CallStatusTag>
        )}
        {callWithAirAmbulanceRequest && (
          <AirAmbulanceStatus data-name="air-ambulance-status">
            {callStatusDisplayName}
          </AirAmbulanceStatus>
        )}

        <TextMessageStatus
          isTT911={isTT911}
          source={source}
          classOfServiceCategory={classOfService}
          unreadTF911Message={unreadTF911Message}
          unreadRcsMessage={unreadRcsMessage}
          isRcsAvailable={isRcsAvailable}
          rcsAcceptStatus={rcsConversation?.data?.accept_status}
          isUnread={isUnread}
          isUnseen={isUnseen}
          tt911Message={tt911Message}
          rcsStatusText={rcsStatusText}
          hasAgent511Access={hasAgent511Access}
        />

        {fullSelectedNumberData?.textFrom911Data?.data?.isOutboundSMS &&
          !unreadTF911Message && <TextMessage>OUTBOUND 911 TEXT</TextMessage>}

        {isConnectedCar && (
          <SiriusXMMessage>Veh. Crash Detected</SiriusXMMessage>
        )}
        {isApple && <PhoneDetails>Add'l Data Avail.</PhoneDetails>}
        {googleIndicator && <PhoneDetails>{googleIndicator}</PhoneDetails>}
        {isCallerIn911informBuilding && (
          <PhoneDetails>Connected Building</PhoneDetails>
        )}
        {isCallerInConnectedSitesBuilding && (
          <ConnectedSiteMessage>Connected Site</ConnectedSiteMessage>
        )}

        {isExpired && (
          <>
            <PhoneDetails>{timeExpired()}</PhoneDetails>
            <PhoneDetails>Right click to remove</PhoneDetails>
          </>
        )}

        <OnsitePersonnelNotifiedText callerId={callerID} />
      </CallBox>
      {callStartTime ? (
        <TimeBox>
          <Time>{displayMilitaryTime(callStartTime)}</Time>
        </TimeBox>
      ) : null}

      <animated.div style={animateProps}>
        <RemoveCall onClick={handleRemoveCall}>Remove</RemoveCall>
      </animated.div>
    </Container>
  );
};

export default EmergencyCall;
